import React, { useEffect, useState } from "react";
import { useClientsManagement } from "../../contexts/ClientsManagementContext";
import { useNavigate } from "react-router-dom";
import Loader from "../loading/Loader";
import MainButton from "../button/MainButton";
import { ArrowLeft, UserSquare } from "iconsax-react";
import { ServiceResultItem } from "../../utilities/types/clients/types";
import ClientServiceResultModal from "./ClientServiceResultModal";
import CustomPagination from "../table/CustomPagination";
import MobileServiceResultCard from "./MobileServiceResultCard";

interface ClientDetailsContentProps {
  clientId: string;
}

const ClientDetailsContent: React.FC<ClientDetailsContentProps> = ({ clientId }) => {
  const { clients, clientServices, loading, loadingServices, error, servicesCurrentPage, servicesTotalPages, fetchClients, fetchClientServices } = useClientsManagement();
  const navigate = useNavigate();
  const [selectedServiceResult, setSelectedServiceResult] = useState<ServiceResultItem | null>(null);
  const hasPreviousPage = servicesCurrentPage > 1;
  const hasNextPage = servicesCurrentPage < servicesTotalPages;

  useEffect(() => {
    if (clients.length === 0) {
      fetchClients();
    }
  }, [clients.length]);

  const client = clients.find((c) => c.id === parseInt(clientId));

  useEffect(() => {
    if (client) {
      fetchClientServices(client.ref_id, servicesCurrentPage);
    }
  }, [client, servicesCurrentPage]);

  const handlePageChange = (newPage: number) => {
    if (client) {
      fetchClientServices(client.ref_id, newPage);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Loader />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50">
        <div className="w-full p-4">
          <div className="flex items-center gap-3">
            <MainButton type="ghost" onClick={() => navigate("/dashboard/clients")}>
              <ArrowLeft className="size-5" variant="Broken" />
            </MainButton>
            <h2 className="font-bold text-closed-shutter-300">Client Details</h2>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center p-8 lg:p-12">
          <UserSquare className="mb-4 size-20 text-gorgonzola-blue-200" variant="Broken" />
          <h3 className="mb-2 text-base font-bold text-closed-shutter-300">Client Not Found</h3>
          <p className="mb-6 text-sm text-center text-closed-shutter-300/70">The client you're looking for doesn't exist or has been removed.</p>
          <MainButton type="primary" onClick={() => navigate("/dashboard/clients")}>
            <ArrowLeft className="size-5" variant="Broken" />
            <span>Back to Clients</span>
          </MainButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50">
      <div className="w-full p-4">
        <div className="flex items-center gap-3">
          <MainButton type="ghost" onClick={() => navigate("/dashboard/clients")}>
            <ArrowLeft className="size-5" variant="Broken" />
          </MainButton>
          <h2 className="font-bold text-closed-shutter-300">Client Details</h2>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-col overflow-hidden border divide-y rounded-lg divide-gorgonzola-blue-50 border-gorgonzola-blue-50">
          <div className="p-4 bg-gorgonzola-blue-50/30">
            <h3 className="text-sm font-bold text-closed-shutter-300">Client Information</h3>
          </div>

          <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-2 lg:gap-8">
            <div className="flex flex-col gap-2">
              <span className="text-xs text-closed-shutter-300/70">Client Name</span>
              <span className="text-sm font-bold text-closed-shutter-300">{client.name}</span>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-xs text-closed-shutter-300/70">Reference ID</span>
              <span className="font-mono text-sm font-bold text-closed-shutter-300">{client.ref_id}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        {error && <div className="p-4 mb-4 text-sm border rounded-lg bg-english-vermillion-50 border-english-vermillion-300 text-english-vermillion-300">{error}</div>}

        <div className="flex flex-col overflow-hidden border divide-y rounded-lg divide-gorgonzola-blue-50 border-gorgonzola-blue-50">
          <div className="p-4 bg-gorgonzola-blue-50/30">
            <h3 className="text-sm font-bold text-closed-shutter-300">Service Usage</h3>
          </div>

          {loadingServices ? (
            <div className="flex items-center justify-center py-12">
              <Loader />
            </div>
          ) : clientServices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <UserSquare className="mb-4 size-16 text-gorgonzola-blue-200" variant="Broken" />
              <h3 className="mb-2 text-sm font-bold text-closed-shutter-300">No Service Usage Yet</h3>
              <p className="text-xs text-closed-shutter-300/70">This client hasn't used any services yet</p>
            </div>
          ) : (
            <>
              {/* Mobile View */}
              <div className="block md:hidden odd:*:bg-gorgonzola-blue-50 even:*:bg-white">
                {clientServices.map((service) => (
                  <MobileServiceResultCard key={service.id} service={service} onClick={() => setSelectedServiceResult(service)} />
                ))}
              </div>

              {/* Desktop View */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-gorgonzola-blue-50/50">
                    <tr className="*:px-4 *:py-3 *:text-left *:text-xs *:font-bold *:text-closed-shutter-300">
                      <th>Service</th>
                      <th>Status</th>
                      <th>Message</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gorgonzola-blue-50">
                    {clientServices.map((service) => (
                      <tr key={service.id} onClick={() => setSelectedServiceResult(service)} className="transition-colors cursor-pointer hover:bg-gorgonzola-blue-50/30 *:px-4 *:py-3">
                        <td className="font-medium text-closed-shutter-300">{service.service_name}</td>
                        <td>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${service.status === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                            {service.status}
                          </span>
                        </td>
                        <td className="text-closed-shutter-300/70">{service.message}</td>
                        <td className="text-closed-shutter-300/70">{new Date(service.created_at * 1000).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {clientServices.length > 0 && (
            <CustomPagination currentPage={servicesCurrentPage} totalPages={servicesTotalPages} hasPreviousPage={hasPreviousPage} hasNextPage={hasNextPage} onPageChange={handlePageChange} />
          )}
        </div>
      </div>

      {/* Service Result Modal */}
      <ClientServiceResultModal serviceResult={selectedServiceResult} onClose={() => setSelectedServiceResult(null)} />
    </div>
  );
};

export default ClientDetailsContent;
